<template>
  <div class="statistics-container">
    <!-- 顶部数据卡片 -->
    <el-row :gutter="20" class="data-cards">
      <el-col :span="6" v-for="(item, index) in statisticsCards" :key="index">
        <el-card shadow="hover">
          <template #header>
            <div class="card-header">
              <span>{{ item.title }}</span>
              <el-tag size="small" :type="item.tagType">{{
                item.change
              }}</el-tag>
            </div>
          </template>
          <div class="card-content">
            <span class="number">{{ item.value }}</span>
            <span class="unit">{{ item.unit }}</span>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 图表区域 -->
    <div class="charts-container">
      <el-row :gutter="20">
        <!-- 左侧趋势图 -->
        <el-col :span="24">
          <el-card class="chart-card">
            <template #header>
              <div class="chart-header">
                <span>上传文件趋势</span>
              </div>
            </template>
            <div style="width: 100%; height: 350px">
              <Echarts :option="trendOption" autoresize />
            </div>
          </el-card>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { auditFilesByDay } from "../../api/index";
import type { EChartsOption } from "echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { LineChart, PieChart } from "echarts/charts";
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from "echarts/components";
import VChart from "vue-echarts";
import dayjs from "dayjs";

use([
  CanvasRenderer,
  LineChart,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
]);

// 将 Echarts 组件重命名为 VChart
const Echarts = VChart;

// 统计数据变量
const todayData = ref({ total: 0, change: "+0%", tagType: "success" });
const sevenDaysData = ref({ total: 0, change: "+0%", tagType: "success" });
const thirtyDaysData = ref({ total: 0, change: "+0%", tagType: "success" });
const sixtyDaysData = ref({ total: 0, change: "+0%", tagType: "success" });

// 修改统计卡片数据结构
const statisticsCards = ref([
  {
    title: "今日文件数",
    value: computed(() => todayData.value.total.toString()),
    unit: "个",
    change: computed(() => todayData.value.change),
    tagType: computed(() => todayData.value.tagType),
  },
  {
    title: "最近7天文件数",
    value: computed(() => sevenDaysData.value.total.toString()),
    unit: "个",
    change: computed(() => sevenDaysData.value.change),
    tagType: computed(() => sevenDaysData.value.tagType),
  },
  {
    title: "最近30天文件数",
    value: computed(() => thirtyDaysData.value.total.toString()),
    unit: "个",
    change: computed(() => thirtyDaysData.value.change),
    tagType: computed(() => thirtyDaysData.value.tagType),
  },
  {
    title: "最近60天文件数",
    value: computed(() => sixtyDaysData.value.total.toString()),
    unit: "个",
    change: computed(() => sixtyDaysData.value.change),
    tagType: computed(() => sixtyDaysData.value.tagType),
  },
]);

// 趋势图配置
const trendOption = ref<EChartsOption>({
  tooltip: {
    trigger: "axis",
  },
  grid: {
    left: "3%",
    right: "4%",
    bottom: "3%",
    containLabel: true,
  },
  legend: {
    data: ["总量"],
  },
  xAxis: {
    type: "category",
    boundaryGap: false,
    data: [],
    axisLabel: {
      rotate: 45,
    },
  },
  yAxis: {
    type: "value",
  },
  series: [
    {
      name: "总量",
      type: "line",
      data: [],
      smooth: true,
      itemStyle: {
        color: "#409EFF",
      },
    },
  ],
});

// 状态分布图配置
const statusOption = ref<EChartsOption>({
  tooltip: {
    trigger: "item",
    formatter: "{b}: {c} ({d}%)",
  },
  legend: {
    orient: "vertical",
    right: 10,
    top: "center",
  },
  series: [
    {
      type: "pie",
      radius: ["50%", "70%"],
      avoidLabelOverlap: false,
      itemStyle: {
        borderRadius: 10,
        borderColor: "#fff",
        borderWidth: 2,
      },
      label: {
        show: false,
        position: "center",
      },
      emphasis: {
        label: {
          show: true,
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      labelLine: {
        show: false,
      },
      data: [
        { value: 0, name: "合规" },
        { value: 0, name: "无感水印中" },
        { value: 0, name: "异常" },
      ],
    },
  ],
});

// 独立的数据获取函数
const fetchSevenDaysData = async () => {
  const end = dayjs().add(1, "days").format("YYYY-MM-DD");
  const start = dayjs().subtract(7, "days").format("YYYY-MM-DD");
  try {
    const res = await auditFilesByDay({
      start_date: start,
      end_date: end,
    });
    if (res?.code === "success") {
      updateSevenDaysData(res.content.data);
    }
  } catch (error) {
    console.error("获取7天数据失败:", error);
  }
};

const fetchThirtyDaysData = async () => {
  const end = dayjs().add(1, "days").format("YYYY-MM-DD");
  const start = dayjs().subtract(30, "days").format("YYYY-MM-DD");
  try {
    const res = await auditFilesByDay({
      start_date: start,
      end_date: end,
    });
    if (res?.code === "success") {
      updateThirtyDaysData(res.content.data);
    }
  } catch (error) {
    console.error("获取30天数据失败:", error);
  }
};

const fetchSixtyDaysData = async () => {
  const end = dayjs().add(1, "days").format("YYYY-MM-DD");
  const start = dayjs().subtract(60, "days").format("YYYY-MM-DD");
  try {
    const res = await auditFilesByDay({
      start_date: start,
      end_date: end,
    });
    if (res?.code === "success") {
      updateSixtyDaysData(res.content.data);
    }
  } catch (error) {
    console.error("获取60天数据失败:", error);
  }
};

// 添加趋势图数据获取函数
const fetchTrendData = async () => {
  const end = dayjs().add(1, "days").format("YYYY-MM-DD");
  const start = dayjs().subtract(2, "month").format("YYYY-MM-DD");

  try {
    const res = await auditFilesByDay({
      start_date: start,
      end_date: end,
    });
    if (res?.code === "success") {
      updateChartData(res.content.data);
    }
  } catch (error) {
    console.error("获取趋势图数据失败:", error);
  }
};

// 数据更新函数
const calculateChange = (current: number, prev: number) => {
  if (prev === 0) return "+0%";
  const change = Number((((current - prev) / prev) * 100).toFixed(1));
  return change > 0 ? `+${change}%` : `${change}%`;
};

const updateSevenDaysData = (data: any[]) => {
  const last7DaysTotal = data
    .slice(0, 7)
    .reduce((acc, cur) => acc + cur.total_count, 0);
  const previous7DaysTotal = data
    .slice(7, 14)
    .reduce((acc, cur) => acc + cur.total_count, 0);
  const change = calculateChange(last7DaysTotal, previous7DaysTotal);

  sevenDaysData.value = {
    total: last7DaysTotal,
    change,
    tagType: last7DaysTotal >= previous7DaysTotal ? "success" : "warning",
  };
};

const updateThirtyDaysData = (data: any[]) => {
  const last30DaysTotal = data
    .slice(0, 30)
    .reduce((acc, cur) => acc + cur.total_count, 0);
  const previous30DaysTotal = data
    .slice(30, 60)
    .reduce((acc, cur) => acc + cur.total_count, 0);
  const change = calculateChange(last30DaysTotal, previous30DaysTotal);

  thirtyDaysData.value = {
    total: last30DaysTotal,
    change,
    tagType: last30DaysTotal >= previous30DaysTotal ? "success" : "danger",
  };
};

const updateSixtyDaysData = (data: any[]) => {
  const last60DaysTotal = data
    .slice(0, 60)
    .reduce((acc, cur) => acc + cur.total_count, 0);
  const previous60DaysTotal = data
    .slice(60, 120)
    .reduce((acc, cur) => acc + cur.total_count, 0);
  const change = calculateChange(last60DaysTotal, previous60DaysTotal);

  sixtyDaysData.value = {
    total: last60DaysTotal,
    change,
    tagType: last60DaysTotal >= previous60DaysTotal ? "success" : "danger",
  };
};

// 更新图表数据
const updateChartData = (data: any[]) => {
  if (!Array.isArray(data) || data.length === 0) return;

  const dates: string[] = [];
  const totalCounts: number[] = [];

  data.forEach((item) => {
    const date = new Date(item.date);
    dates.push(`${date.getMonth() + 1}/${date.getDate()}`);
    totalCounts.push(Number(item.total_count));
  });

  // 更新趋势图配置
  trendOption.value.xAxis.data = dates;
  trendOption.value.series = [
    {
      name: "总量",
      type: "line",
      data: totalCounts,
      smooth: true,
      itemStyle: { color: "#409EFF" },
    },
  ];
};

// 刷新数据的函数
const refreshData = () => {
  fetchSevenDaysData();
  fetchThirtyDaysData();
  fetchSixtyDaysData();
  fetchTrendData();
};

onMounted(() => {
  console.log("onMounted refreshData");
  refreshData();
});
</script>

<style scoped>
.statistics-container {
  padding: 20px;
  background-color: #f5f7fa;
}

.data-cards {
  margin-bottom: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.card-content {
  text-align: center;
}

.number {
  font-size: 24px;
  font-weight: bold;
  margin-right: 4px;
}

.unit {
  font-size: 14px;
  color: #909399;
}

.chart-card {
  margin-bottom: 20px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
